import {
  forwardRef,
  memo,
  type ReactNode,
  useImperativeHandle,
  useRef,
  useMemo,
  useState
} from 'react';

import cx from 'clsx';
import { RiArrowDownSLine } from 'react-icons/ri';

import type { Country } from '@common/types';
import {
  Box,
  type BoxProps,
  Combobox,
  Group,
  Input,
  type MantineSize,
  NumberInput,
  ScrollArea,
  Text,
  UnstyledButton,
  useCombobox
} from '@mantine/core';

import { InputLoader } from '../../feedback';
import { InputOverrideClassNames } from '../../inputs/textinput/TextInput.styles';
import { Flag } from '../../misc';

import classes from './PhoneNumberSelectGroup.module.css';

export interface PhoneNumberSelectGroupProps extends BoxProps {
  /**
   * name - field name for the phone number input
   */
  name?: string;
  /**
   * countryValue - The selected country object (full object with name, calling_code, code)
   * This solves ambiguity issues where multiple countries share the same calling code (e.g., USA/Canada both use +1)
   */
  countryValue?: Country;

  /**
   * onCountryChange - Callback when country is selected
   * Receives the full country object
   */
  onCountryChange?: (country: Country) => void;

  /**
   * size of the component
   */
  size?: MantineSize;

  /**
   * value - Just the phone number (e.g., "9840471601")
   * NOT the combined format like "+91-9840471601"
   */
  value?: string;

  /**
   * onChange - Callback when phone number changes
   * Receives just the phone number string
   */
  onChange?: (value: string) => void;

  /**
   * Label for the component
   */
  label?: ReactNode;

  /**
   * Placeholder for the number input
   */
  placeholder?: string;

  /**
   * Description for the component
   */
  description?: string;

  /**
   * show/hide loading indicator
   */
  loading?: boolean;

  /**
   * show error
   */
  error?: string;

  /**
   * disables both select and number input
   */
  disabled?: boolean;

  /**
   * only disables the country code select
   */
  disableCountryCode?: boolean;

  /**
   * onblur for the number input
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;

  /**
   * onfocus for the number input
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;

  /**
   * List of countries
   */
  countries?: Country[];

  /**
   * Maximum length of the phone number (without country code)
   */
  maxLength?: number;
  /**
   * Whether to show asterisk for required fields
   */
  withAsterisk?: boolean;
}

const PhoneNumberSelectGroup = memo(
  forwardRef<HTMLInputElement, PhoneNumberSelectGroupProps>(
    (
      {
        name,
        countryValue,
        onCountryChange,
        label,
        size = 'xl',
        description,
        error = '',
        value,
        loading,
        placeholder,
        disabled,
        disableCountryCode,
        countries,
        onChange,
        onBlur: onBlurProp,
        onFocus: onFocusProp,
        maxLength,
        withAsterisk,
        ...rest
      },
      ref
    ) => {
      const [search, setSearch] = useState('');
      const [isFocused, setIsFocused] = useState(false);
      const numberInputRef = useRef<HTMLInputElement>(null);

      useImperativeHandle(ref, () => numberInputRef.current as HTMLInputElement);

      const combobox = useCombobox({
        onDropdownClose: () => {
          setSearch('');
          numberInputRef.current?.focus();
        },
        onDropdownOpen: () => {
          combobox.focusSearchInput();
        }
      });

      // Use countryValue prop directly (fully controlled)
      const selectedCountry = countryValue;

      // Filter countries based on search
      const filteredCountries = useMemo(() => {
        if (!search) return countries;

        const searchLower = search.toLowerCase().trim();
        const normalizedSearch = searchLower.replace(/[^a-z0-9]/g, '');

        const filtered = countries?.filter((country) => {
          const countryName = country.name?.toLowerCase();
          const countryCode = country.code?.toLowerCase();
          const callingCode = country.calling_code?.toLowerCase();
          const normalizedCallingCode = callingCode?.replace(/[^a-z0-9]/g, '');

          if (!countryCode || !callingCode) {
            return false;
          }

          return (
            countryName?.includes(searchLower) ||
            countryCode?.includes(searchLower) ||
            callingCode?.includes(searchLower) ||
            (normalizedSearch.length > 0 &&
              normalizedCallingCode?.includes(normalizedSearch))
          );
        });
        return filtered;
      }, [countries, search]);

      const handleCountrySelect = (countryName: string) => {
        const country = countries?.find((c) => c.name === countryName);
        if (country && onCountryChange) {
          onCountryChange(country);
        }
        setSearch('');
        combobox.closeDropdown();
      };

      const onChangePhoneNumber = (phoneValue: string | number) => {
        onChange?.(String(phoneValue));
      };

      const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocusProp?.(event);
      };

      const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlurProp?.(event);
      };

      const rightSectionContent = useMemo(() => {
        if (loading) {
          return <InputLoader size={size} />;
        }

        // Show flag with or without chevron based on disableCountryCode
        if (disableCountryCode) {
          // Just show the flag, no button functionality
          return (
            <Box className={classes.countryFlag} data-disabled>
              {selectedCountry && (
                <Flag
                  country={{
                    name: selectedCountry.name,
                    value: selectedCountry.code
                  }}
                  size="w20"
                  w={20}
                />
              )}
            </Box>
          );
        }

        return (
          <Combobox.EventsTarget>
            <UnstyledButton
              onClick={() => combobox.toggleDropdown()}
              disabled={disabled}
              className={classes.countryButton}
              data-disabled={disabled}
            >
              {selectedCountry && (
                <Flag
                  country={{
                    name: selectedCountry.name,
                    value: selectedCountry.code
                  }}
                  w={20}
                />
              )}
              <RiArrowDownSLine fontSize={16} size={24} />
            </UnstyledButton>
          </Combobox.EventsTarget>
        );
      }, [loading, selectedCountry, disabled, disableCountryCode, size, combobox]);

      const inputClassNames = useMemo(() => {
        const baseClasses = InputOverrideClassNames({
          size,
          focussed: isFocused,
          error: Boolean(error),
          rightSection: rightSectionContent,
          enableRightSectionEvents: !(loading || disableCountryCode)
        });

        return {
          ...baseClasses,
          input: cx(baseClasses.input, classes.input),
          section: cx(baseClasses.section, classes.section)
        };
      }, [size, isFocused, error, rightSectionContent, loading, disableCountryCode]);

      return (
        <Box {...rest} pos="relative" w="100%">
          <Combobox
            store={combobox}
            position="bottom-start"
            width="target"
            withinPortal={false}
            onOptionSubmit={(value) => {
              handleCountrySelect(value);
            }}
          >
            {label && (
              <Input.Label
                className={classes.label}
                size={size}
                required={withAsterisk}
              >
                {label}
              </Input.Label>
            )}
            {description && (
              <Input.Description size={size} mb={4}>
                {description}
              </Input.Description>
            )}
            <Combobox.DropdownTarget>
              <NumberInput
                ref={numberInputRef}
                name={name}
                value={value || ''}
                onChange={onChangePhoneNumber}
                onBlur={handleBlur}
                onFocus={handleFocus}
                disabled={disabled}
                placeholder={placeholder}
                maxLength={maxLength}
                type="tel"
                datatype="numbersOnly"
                autoComplete="on"
                size={size}
                error={error}
                allowNegative={false}
                leftSection={
                  disableCountryCode ? (
                    <Text size="sm" fw={500} className={classes.callingCode}>
                      +{selectedCountry?.calling_code || ''}
                    </Text>
                  ) : (
                    <Combobox.EventsTarget targetType="button">
                      <UnstyledButton
                        onClick={() => combobox.toggleDropdown()}
                        disabled={disabled}
                        className={classes.callingCode}
                      >
                        <Text size="sm" fw={500}>
                          +{selectedCountry?.calling_code || ''}
                        </Text>
                      </UnstyledButton>
                    </Combobox.EventsTarget>
                  )
                }
                leftSectionPointerEvents={
                  loading || disableCountryCode ? 'none' : 'all'
                }
                rightSection={rightSectionContent}
                rightSectionPointerEvents={
                  loading || disableCountryCode ? 'none' : 'all'
                }
                classNames={inputClassNames}
              />
            </Combobox.DropdownTarget>

            {/* Only render dropdown if country code selection is enabled */}
            {!disableCountryCode && (
              <Combobox.Dropdown className={classes.dropdown}>
                <Combobox.Search
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  placeholder="Search countries..."
                  className={classes.search}
                  size="xl"
                />
                <Combobox.Options>
                  <ScrollArea.Autosize
                    mah={{
                      base: 200,
                      xs: 150
                    }}
                    type="scroll"
                  >
                    {filteredCountries && filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <Combobox.Option
                          key={`${country.name}_${country.code}_${country.calling_code}_${index}`}
                          value={country.name}
                          className={classes.option}
                        >
                          <Group wrap="nowrap" justify="space-between" gap="xs">
                            <Text
                              style={{
                                flex: 1,
                                flexBasis: '70%',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflow: 'wrap'
                              }}
                            >
                              {country.name}
                            </Text>
                            <Group wrap="nowrap">
                              <Text>{country.calling_code}</Text>
                              <Flag
                                country={{
                                  name: country.name,
                                  value: country.code
                                }}
                                size="w20"
                                w={20}
                              />
                            </Group>
                          </Group>
                        </Combobox.Option>
                      ))
                    ) : (
                      <Combobox.Empty>No countries found</Combobox.Empty>
                    )}
                  </ScrollArea.Autosize>
                </Combobox.Options>
              </Combobox.Dropdown>
            )}
          </Combobox>
        </Box>
      );
    }
  )
);

PhoneNumberSelectGroup.displayName = 'PhoneNumberSelectGroup';

export { PhoneNumberSelectGroup };
